'use client';

import { useEffect, useRef, useState } from 'react';
import { prochitatChernovik, sohranitChernovik } from '@/lib/chernoviki-form';

/**
 * Значения формы, которые сами переживают перезагрузку.
 *
 * Обёртка над `useState`, а не отдельный вызов сохранения в каждой
 * форме: иначе в одной форме сохранение забудут, в другой поставят не
 * туда, и «введённое не теряется» станет обещанием, которое держится в
 * половине случаев. Здесь подключение — замена одной строки состояния.
 *
 * Имя формы (`imya`) обязательно и должно быть своим у каждой формы:
 * общий ключ подставлял бы текст письма в поле озвучки.
 */

/** Сколько ждать после последнего нажатия клавиши перед записью, мс. */
const ZADERZHKA_MS = 400;

export function useChernovikFormy<T extends Record<string, string>>(
  imya: string,
  nachalnye: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  /**
   * Первый кадр рисуется без черновика, даже когда он есть.
   *
   * Серверная разметка не знает про localStorage, и подставь мы текст
   * сразу, React пожаловался бы на расхождение и перерисовал поддерево.
   * Поэтому черновик приезжает в эффекте, сразу после монтирования:
   * человек видит его в тот же момент, а гидратация остаётся честной.
   */
  const [znacheniya, setZnacheniya] = useState<T>(nachalnye);

  // Что именно мы уже прочитали. Ref, а не состояние: значение нужно
  // эффекту сохранения, чтобы не записать пустоту поверх черновика в
  // первый же проход, а перерисовка от него не требуется.
  const prochitano = useRef(false);

  useEffect(() => {
    const chernovik = prochitatChernovik(imya);
    prochitano.current = true;

    if (Object.keys(chernovik).length === 0) return;

    // Слияние, а не замена: у формы есть поля со значением по умолчанию
    // (выбранный жанр, язык озвучки), и замена целиком сбросила бы их в
    // пустоту, если черновик писался до появления этого поля.
    //
    // Черновик при этом не перебивает то, что человек успел набрать за
    // время до чтения: такие значения уже не равны начальным.
    setZnacheniya((prezhnie) => {
      const slitye = { ...prezhnie };
      for (const [pole, znachenie] of Object.entries(chernovik)) {
        const teper = prezhnie[pole];
        const nachalnoe = nachalnye[pole];
        if (teper === undefined || teper === '' || teper === nachalnoe) {
          (slitye as Record<string, string>)[pole] = znachenie;
        }
      }
      return slitye;
    });
    // Зависимость только от имени формы: `nachalnye` пересоздаётся на
    // каждом рендере вызывающего кода, и подписка на него перечитывала
    // бы черновик бесконечно.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imya]);

  useEffect(() => {
    // Пока черновик не прочитан, писать нельзя: первый проход эффекта
    // сохранил бы начальные (пустые) значения и стёр бы то, что человек
    // набрал в прошлый раз.
    if (!prochitano.current) return;

    // Запись с задержкой, а не на каждое нажатие: localStorage
    // синхронный, и сериализация двадцати тысяч знаков на каждую букву
    // подвешивала бы набор текста на слабом телефоне.
    const tajmer = setTimeout(() => {
      sohranitChernovik(imya, znacheniya);
    }, ZADERZHKA_MS);

    return () => {
      clearTimeout(tajmer);
    };
  }, [imya, znacheniya]);

  return [znacheniya, setZnacheniya];
}
